"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";

interface SearchResult {
  id: string;
  name: string;
  logoUrl: string | null;
  slug: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  onClose: () => void;
  isOpen: boolean;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function getColorForName(name: string): string {
  const colorPalette = [
    "bg-blue-600",
    "bg-purple-600",
    "bg-green-600",
    "bg-indigo-600",
    "bg-teal-600",
    "bg-pink-600",
    "bg-orange-600",
    "bg-red-600",
    "bg-yellow-600",
    "bg-cyan-600",
    "bg-violet-600",
    "bg-emerald-600",
    "bg-rose-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorPalette[Math.abs(hash) % colorPalette.length];
}

export function SearchResults({ results, onClose, isOpen }: SearchResultsProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || results.length === 0) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
    >
      {results.map((company) => (
        <Link
          key={company.id}
          href={`/company/${company.slug}`}
          onClick={onClose}
          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
        >
          {company.logoUrl ? (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 flex-shrink-0">
              <img
                src={company.logoUrl}
                alt={company.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            </div>
          ) : (
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${getColorForName(
                company.name
              )}`}
            >
              {getInitials(company.name)}
            </div>
          )}
          <span className="font-medium text-gray-900">{company.name}</span>
        </Link>
      ))}
    </div>
  );
}

